/// <reference types="vite/client" />
/**
 * 配る版を作るときに組み込む会社の色と名前（ログインの画面を、初回から会社の色で出すため）。
 *
 * 会社ごとの表示設定（`GET /org/branding`）はログインしてからでないと読めません。
 * そのため、**配る版を作るとき**に色と名前を組み込めるようにしてあります（README「配る版の作り方」）。
 *
 * ```
 * VITE_BRAND_COLOR_SEED=#E12F27 VITE_BRAND_APP_NAME=現場チャット npm run build
 * ```
 *
 * - 組み込むのは**色（`#RRGGBB`）と会社・アプリの名前だけ**です。お名前・電話番号などの個人の情報は入れません。
 * - 組み込まなければ、これまでどおり中立の灰色・「管理画面」だけの表示です（コードに会社の色を書かない・決定21）。
 * - ログインの画面の色は「この端末で前に読めた色 → 組み込んだ色 → 灰色」の順に使います（アプリと同じ順）。
 *   ログインしたあとは、いつも会社の設定（サーバ）が正です。
 */

const HEX = /^#[0-9a-fA-F]{6}$/;

/** 組み立てのときに Vite が値を埋め込む（`import.meta.env.VITE_…` の形で書いたものだけが置き換わる）。 */
export interface BuildBrandEnv {
  VITE_BRAND_COLOR_SEED?: unknown;
  VITE_BRAND_APP_NAME?: unknown;
}

function builtEnv(): BuildBrandEnv {
  // 1つずつ `import.meta.env.VITE_…` と書く（まとめて取り出すと、組み立てで値が埋め込まれない）
  return {
    VITE_BRAND_COLOR_SEED: import.meta.env.VITE_BRAND_COLOR_SEED,
    VITE_BRAND_APP_NAME: import.meta.env.VITE_BRAND_APP_NAME,
  };
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** 組み込んだ会社の色。形が違う・組み込んでいないときは空。 */
export function buildBrandColor(env: BuildBrandEnv = builtEnv()): string {
  const value = text(env.VITE_BRAND_COLOR_SEED);
  return HEX.test(value) ? value : '';
}

/** 組み込んだアプリ（会社）の名前。組み込んでいないときは空。長すぎるものは使わない。 */
export function buildAppName(env: BuildBrandEnv = builtEnv()): string {
  const value = text(env.VITE_BRAND_APP_NAME);
  return value.length > 0 && value.length <= 40 ? value : '';
}
